import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, Skeleton, Typography, useTheme } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { IoIosArrowDown } from "react-icons/io";
import { FaSquareFull } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { useGetPlanActualQuery } from "store/slices/apis/costSlice";


const FundRequirement = (props) => {
    const { cpiData } = props
    const theme = useTheme();
    const { projectId } = useParams();

    const [overAll, setOverAll] = useState(100);
    const [planned, setPlanned] = useState(0);
    const [actual, setActual] = useState(0);

    const actualPlanRes = useGetPlanActualQuery({ projectId });

    useEffect(() => {
        if (actualPlanRes?.isSuccess) {
            const resdata = actualPlanRes?.data?.data;
            setActual(resdata?.actualPercent ? resdata?.actualPercent : 0);
            setPlanned(resdata?.planPercent ? resdata?.planPercent : 0);
        }
        cpiData(actualPlanRes)
    }, [actualPlanRes])

    let options = {
        chart: {
            type: "solidgauge",
            height: 120,
            width: 120,
            backgroundColor: 'transparent',
            marginBottom: 35,
            marginTop: 0
        },
        title: {
            text: "",
            style: { fontSize: "18px" },
        },
        pane: {
            startAngle: 0,
            endAngle: 360,
            size: '100%',
            background: [
                {
                    outerRadius: "",
                    innerRadius: "88%",
                    backgroundColor: Highcharts.color("#7cb5ec").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "63%",
                    backgroundColor: Highcharts.color("#90ed7d").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "38%",
                    backgroundColor: Highcharts.color("#f7a35c").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
            ],
        },
        yAxis: {
            min: 0,
            max: 100,
            lineWidth: 0,
            tickPositions: [],
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    enabled: false,
                },
                linecap: "round",
                stickyTracking: false,
            },
        },
        legend: {
            enabled: true,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
        },
        series: [
            {
                name: "Overall Progress",
                data: [{ y: overAll, color: "#E8E3E3", radius: "90%", innerRadius: "100%" }],
            },
            {
                name: "Planned Progress",
                data: [{ y: planned, color: "#295477", radius: "75%", innerRadius: "85%" }],
            },
            {
                name: "Actual Progress",
                data: [{ y: actual, color: actual >= planned ? '#3E756D' : '#E72B33', radius: "60%", innerRadius: "70%" }],


            },
        ],
        credits: {
            enabled: false,
        },
    };

    return (
        <Box
            sx={{
                p: 2,
                width: "100%",
                height: { xs: "auto", md: "207px" },
                maxWidth: "100%",
                minHeight: "100%",
                border: "1px solid #2525252E",
                mt: 0,
                borderRadius: "7px",
                overflow: "hidden",
                backgroundColor: "white",
            }}
        >
            <Box>
                <Typography
                    variant="body"
                    sx={{
                        fontWeight: 500,
                        fontSize: "18px",
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                        color: `${theme?.customSettings?.themeColor}`,
                    }}
                >
                    Financial Progress
                </Typography>
                <Typography
                    sx={{
                        mt: "5px",
                        color: "#5052588A",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                    }}
                >
                    Planned and actual progress to monitor schedule adherence and execution efficiency
                </Typography>
            </Box>
            {/* <Box sx={{ width: "100%", mt: "11px" }}>
                <Select
                    labelId="demo-simple-select-standard-label"
                    id="demo-simple-select-standard"
                    value={10}
                    IconComponent={IoIosArrowDown}
                    sx={{
                        backgroundColor: "#E8E8E8",
                        "& .MuiOutlinedInput-input": {
                            padding: "5px 15px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                        },
                    }}
                >
                    <MenuItem value={10} sx={{ fontSize: "16px" }}>Current month</MenuItem>
                    <MenuItem value={11}>Prev month</MenuItem>
                </Select>
            </Box> */}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: { xs: 2, sm: 0 },
                    mt: "14px",
                }}
            >
                <List sx={{ padding: 0, mt: 0, width: { xs: "100%", sm: "auto" } }}>
                    {options?.series
                        ?.filter((val) => val.name !== "Overall Progress")
                        .map((val, index) => (
                            <ListItem key={index} sx={{ paddingLeft: 0 }}>
                                <ListItemIcon sx={{ minWidth: '20px' }}>
                                    <FaSquareFull
                                        style={{
                                            color: `${val?.data[0]?.color}`,
                                            height: '14px',
                                            width: '14px',
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={`${val?.name}: ${val?.data[0]?.y}%`}
                                    sx={{
                                        color: "#182A4E",
                                        whiteSpace: 'nowrap',
                                        marginLeft: "8px",
                                        '& .MuiTypography-root': {
                                            fontWeight: 500,
                                            fontSize: '16px',
                                            lineHeight: '100%',
                                            letterSpacing: '-0.4px',
                                        },
                                    }}
                                />
                            </ListItem>
                        ))}
                </List>
                <Box sx={{
                    flexShrink: 0,
                    width: { xs: "100%", sm: "auto" },
                    display: "flex",
                    justifyContent: { xs: "center", sm: "flex-end" },
                }}>
                    {actualPlanRes?.isLoading ? (
                        <Skeleton variant="circular" width={130} height={130} animation="wave" sx={{ marginTop: '10px' }} />
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    )}
                </Box>
            </Box >
        </Box>

    )

};


export default FundRequirement;
